/**
 * Navigate - open/close state for the app's launcher (the logo menu).
 *
 * Lives outside Header.tsx (2026-09-30, the navigatemenu overhaul) because
 * more than the logo opens it now: Ctrl/Cmd+K from anywhere, and the fit
 * bar on a screen where the whole shell - logo included - is scaled too small
 * to tap. It also has to be readable by App's `anyModalOpen()`, which it was
 * not while the signal was local to the header: an open Inspector sat on top
 * of Navigate's right edge, where PANIC and the close button are.
 */

import { createSignal } from 'solid-js';
import { navigateDocked, dockHidden, setDockHidden } from './dock.ts';

const [open, setOpen] = createSignal(false);
// Opened from the keyboard: focus the search even on a screen that would
// otherwise skip autofocus (an iPad with a keyboard but no trackpad reports
// `hover: none`, and still typed Cmd+K to get here).
const [wantsSearch, setWantsSearch] = createSignal(false);

export const navigateOpen = open;
export const navigateWantsSearch = wantsSearch;
export function clearNavigateWantsSearch(): void { setWantsSearch(false); }

export function openNavigate(opts: { focusSearch?: boolean } = {}): void {
  setWantsSearch(!!opts.focusSearch);
  if (navigateDocked()) setDockHidden(false);
  setOpen(true);
}
export function closeNavigate(): void { setOpen(false); setPaneSig('home'); }

/* THE SECTION NAVIGATE SHOWS (2026-10-05, app-study.html: "every destination
   opens IN it"). Sections that have their own open switch elsewhere
   (Settings, Connect, Pedals, Rig States, Lights, Pages, Pre-flight) are
   mapped onto this by App, so every way in - the OPBridge pill, the
   Inspector's links, the page pills - lands here. The rest are set directly. */
export type NavPane =
  | 'home' | 'project' | 'pages' | 'add' | 'timelines' | 'audio' | 'rigstates'
  | 'connect' | 'devices' | 'lights' | 'matrix'
  | 'look' | 'preflight' | 'backup' | 'settings' | 'help';
const [paneSig, setPaneSig] = createSignal<NavPane>('home');
export const navigatePane = paneSig;
export function setNavigatePane(p: NavPane): void { setPaneSig(p); }
/** Open Navigate on a section. */
export function openNavigateAt(p: NavPane): void {
  setPaneSig(p); setWantsSearch(false);
  if (navigateDocked()) setDockHidden(false);
  setOpen(true);
}
/** The logo. Docked on a desktop (navigate/dock.ts, DEC-268) it hides and
 *  shows Navigate's header and sidebar and leaves the section as it is;
 *  anywhere else it opens and closes the full-screen launcher.
 *  Docked, it only works while the canvas is on screen, full window or
 *  inside Navigate (operator, 2026-10-15); over any other section it does
 *  nothing, so it never hides the chrome out from under a section. */
export function toggleNavigate(): void {
  if (navigateDocked()) { if (!open()) setDockHidden(!dockHidden()); return; }
  if (open()) closeNavigate();
  else openNavigate();
}
